import { clsx } from 'clsx';

export interface ProviderBadgeProps {
  provider: string;
  size?: 'xs' | 'sm' | 'md';
  showLabel?: boolean;
}

interface ProviderDef {
  label: string;
  bg: string;
  text: string;
  border: string;
  icon: string;
}

const PROVIDERS: Record<string, ProviderDef> = {
  hubspot:    { label: 'HubSpot',    bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20', icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15v-4H7l6-8v4h4l-6 8z' },
  salesforce: { label: 'Salesforce',  bg: 'bg-sky-500/10',    text: 'text-sky-400',    border: 'border-sky-500/20',    icon: 'M12 2a10 10 0 100 20 10 10 0 000-20zm-2 14.5v-9l7 4.5-7 4.5z' },
  close:      { label: 'Close',       bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/20', icon: 'M18 6L6 18M6 6l12 12' },
  pipedrive:  { label: 'Pipedrive',   bg: 'bg-green-500/10',  text: 'text-green-400',  border: 'border-green-500/20',  icon: 'M12 2v20M2 12h20' },
  zoho:       { label: 'Zoho',        bg: 'bg-red-500/10',    text: 'text-red-400',    border: 'border-red-500/20',    icon: 'M4 4h16v16H4z' },
  quickbooks: { label: 'QuickBooks',  bg: 'bg-emerald-500/10',text: 'text-emerald-400',border: 'border-emerald-500/20',icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 13l-2-2 1.5-1.5L11 13l4-4 1.5 1.5L11 16z' },
  fireflies:  { label: 'Fireflies',   bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20', icon: 'M12 18.5a6.5 6.5 0 100-13 6.5 6.5 0 000 13zM12 2v3m0 14v3m10-10h-3M5 12H2m15.07-5.07l-2.12 2.12M9.05 14.95l-2.12 2.12m10.14 0l-2.12-2.12M9.05 9.05L6.93 6.93' },
  apollo:     { label: 'Apollo',      bg: 'bg-blue-500/10',   text: 'text-blue-400',   border: 'border-blue-500/20',   icon: 'M12 2L2 22h20L12 2zm0 6l6 12H6l6-12z' },
  linear:     { label: 'Linear',      bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/20', icon: 'M3 12a9 9 0 1018 0 9 9 0 00-18 0zm4 0a5 5 0 1010 0' },
  notion:     { label: 'Notion',      bg: 'bg-zinc-500/10',   text: 'text-zinc-400',   border: 'border-zinc-500/20',   icon: 'M4 4h10l6 6v10H4V4zm10 0v6h6' },
  stripe:     { label: 'Stripe',      bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/20', icon: 'M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-7.076-2.19l-.893 5.575C4.753 22.658 7.536 24 11.435 24c2.594 0 4.715-.68 6.216-1.956 1.614-1.365 2.384-3.31 2.384-5.603 0-4.128-2.508-5.779-6.059-7.291z' },
};

const FALLBACK: ProviderDef = {
  label: 'CRM', bg: 'bg-surface-2', text: 'text-ink-dim', border: 'border-border',
  icon: 'M4 6h16M4 12h16M4 18h16',
};

/** Display names for providers that use the fallback style. */
const LABELS: Record<string, string> = {
  gohighlevel: 'GoHighLevel', freshbooks: 'FreshBooks', chargebee: 'Chargebee', recurly: 'Recurly', sap: 'SAP',
  paypal: 'PayPal', shopify: 'Shopify', square: 'Square', woocommerce: 'WooCommerce', xero: 'Xero',
  gmail: 'Gmail', outlook: 'Outlook', calendar: 'Calendar', import: 'CSV import', manual: 'Manual',
};

const SIZE_MAP = {
  xs: { wrapper: 'px-1.5 py-0.5 text-2xs gap-1', icon: 10 },
  sm: { wrapper: 'px-2 py-0.5 text-2xs gap-1', icon: 12 },
  md: { wrapper: 'px-2.5 py-1 text-xs gap-1.5', icon: 14 },
};

export function ProviderBadge({ provider, size = 'xs', showLabel = true }: ProviderBadgeProps) {
  const key = provider.toLowerCase();
  const p = PROVIDERS[key] ?? { ...FALLBACK, label: LABELS[key] ?? provider };
  const s = SIZE_MAP[size];

  return (
    <span className={clsx('inline-flex items-center rounded-full border font-medium', p.bg, p.text, p.border, s.wrapper)}>
      <svg width={s.icon} height={s.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d={p.icon} />
      </svg>
      {showLabel && <span>{p.label}</span>}
    </span>
  );
}
